import React, { useEffect, useReducer, useState } from "react";

import {
  Button,
  Chip,
  IconButton,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Tooltip,
  Box,
  useTheme,
} from "@material-ui/core";
import { DeleteOutline, Edit } from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import Title from "../../components/Title";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import QueueModal from "../../components/QueueModal";
import { toast } from "react-toastify";
import ConfirmationModal from "../../components/ConfirmationModal";
import { socketConnection } from "../../services/socket";

const DAY_EN = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

const getQueueStatus = (schedules) => {
  if (!schedules || schedules.length === 0) return null;
  const now = new Date();
  const dayName = DAY_EN[now.getDay()];
  const todaySched = schedules.find(s => s.weekdayEn === dayName);
  if (!todaySched) return null;
  if (todaySched.startTime === "00:00" && todaySched.endTime === "00:00") return "closed";
  const [sh, sm] = todaySched.startTime.split(":").map(Number);
  const [eh, em] = todaySched.endTime.split(":").map(Number);
  const cur = now.getHours() * 60 + now.getMinutes();
  return cur >= sh * 60 + sm && cur < eh * 60 + em ? "open" : "closed";
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
  },
  table: {
    minWidth: 650,
  },
  colorDot: {
    width: 24,
    height: 24,
    borderRadius: "50%",
    border: `2px solid ${theme.palette.background.paper}`,
    boxShadow: "0 0 0 1px rgba(0,0,0,0.1)",
  },
  orderCell: {
    fontWeight: 600,
    fontSize: 14,
  },
  greetingCell: {
    maxWidth: 300,
    [theme.breakpoints.down("sm")]: {
      maxWidth: 150,
    },
  },
  actionCell: {
    whiteSpace: "nowrap",
  },
  badgeRow: {
    display: "flex",
    gap: 4,
    marginTop: 4,
    flexWrap: "wrap",
  },
  chipBot: {
    height: 18,
    fontSize: "0.65rem",
    backgroundColor: theme.palette.type === "dark" ? "#004d40" : "#e0f2f1",
    color: theme.palette.type === "dark" ? "#80cbc4" : "#00796b",
  },
  chipAI: {
    height: 18,
    fontSize: "0.65rem",
    backgroundColor: theme.palette.type === "dark" ? "#4a148c" : "#ede7f6",
    color: theme.palette.type === "dark" ? "#ce93d8" : "#6a1b9a",
  },
  chipOpen: {
    height: 18,
    fontSize: "0.65rem",
    backgroundColor: theme.palette.type === "dark" ? "#1b5e20" : "#e8f5e9",
    color: theme.palette.type === "dark" ? "#a5d6a7" : "#2e7d32",
  },
  chipClosed: {
    height: 18,
    fontSize: "0.65rem",
    backgroundColor: theme.palette.type === "dark" ? "#b71c1c" : "#ffebee",
    color: theme.palette.type === "dark" ? "#ef9a9a" : "#c62828",
  },
}));

const QueueBadges = ({ queue, classes }) => {
  const schedStatus = getQueueStatus(queue.schedules);
  if (!queue.integrationId && !queue.promptId && !schedStatus) return null;
  return (
    <div className={classes.badgeRow}>
      {queue.integrationId && (
        <Chip size="small" label="Bot" className={classes.chipBot} />
      )}
      {queue.promptId && (
        <Chip size="small" label="IA" className={classes.chipAI} />
      )}
      {schedStatus === "open" && (
        <Chip size="small" label="Aberto" className={classes.chipOpen} />
      )}
      {schedStatus === "closed" && (
        <Chip size="small" label="Fechado" className={classes.chipClosed} />
      )}
    </div>
  );
};

const reducer = (state, action) => {
  if (action.type === "LOAD_QUEUES") {
    const queues = action.payload;
    const newQueues = [];

    queues.forEach((queue) => {
      const queueIndex = state.findIndex((q) => q.id === queue.id);
      if (queueIndex !== -1) {
        state[queueIndex] = queue;
      } else {
        newQueues.push(queue);
      }
    });

    return [...state, ...newQueues];
  }

  if (action.type === "UPDATE_QUEUES") {
    const queue = action.payload;
    const queueIndex = state.findIndex((u) => u.id === queue.id);

    if (queueIndex !== -1) {
      state[queueIndex] = queue;
      return [...state];
    } else {
      return [queue, ...state];
    }
  }

  if (action.type === "DELETE_QUEUE") {
    const queueId = action.payload;
    const queueIndex = state.findIndex((q) => q.id === queueId);
    if (queueIndex !== -1) {
      state.splice(queueIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }

  return state;
};

const Queues = () => {
  const classes = useStyles();
  const theme = useTheme();

  const [queues, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);
  const [queueModalOpen, setQueueModalOpen] = useState(false);
  const [selectedQueue, setSelectedQueue] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 600);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/queue");
        dispatch({ type: "LOAD_QUEUES", payload: data });
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketConnection({ companyId });

    socket.on(`company-${companyId}-queue`, (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_QUEUES", payload: data.queue });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_QUEUE", payload: data.queueId });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  const handleOpenQueueModal = () => {
    setQueueModalOpen(true);
    setSelectedQueue(null);
  };

  const handleCloseQueueModal = () => {
    setQueueModalOpen(false);
    setSelectedQueue(null);
  };

  const handleEditQueue = (queue) => {
    setSelectedQueue(queue);
    setQueueModalOpen(true);
  };

  const handleCloseConfirmationModal = () => {
    setConfirmModalOpen(false);
    setSelectedQueue(null);
  };

  const handleDeleteQueue = async (queueId) => {
    try {
      await api.delete(`/queue/${queueId}`);
      toast.success("Departamento excluído com sucesso!");
      dispatch({ type: "DELETE_QUEUE", payload: queueId });
    } catch (error) {
      console.error("Erro ao excluir departamento:", error);
      let message = "Falha ao excluir o departamento.";

      if (error.response?.data?.error) {
        message = error.response.data.error;
      } else if (error.response?.status === 409) {
        message =
          "Não foi possível excluir. Verifique se há PROMPT ou INTEGRAÇÃO vinculados.";
      }

      toastError(message);
    } finally {
      setConfirmModalOpen(false);
      setSelectedQueue(null);
    }
  };

  return (
    <MainContainer>
      {/* Modais */}
      <ConfirmationModal
        title={
          selectedQueue &&
          `${i18n.t("queues.confirmationModal.deleteTitle")} ${selectedQueue.name}?`
        }
        open={confirmModalOpen}
        onClose={handleCloseConfirmationModal}
        onConfirm={() => handleDeleteQueue(selectedQueue.id)}
      >
        {i18n.t("queues.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <QueueModal
        open={queueModalOpen}
        onClose={handleCloseQueueModal}
        queueId={selectedQueue?.id}
      />

      {/* Cabeçalho */}
      <MainHeader>
        <Title>{i18n.t("queues.title")}</Title>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenQueueModal}
          >
            {i18n.t("queues.buttons.add")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      {/* Conteúdo Principal (Mobile ou Desktop) */}
      <Paper className={classes.mainPaper} variant="outlined">
        {isMobile ? (
          /* LAYOUT MOBILE: Cards */
          <Box>
            {loading ? (
              [...Array(3)].map((_, i) => (
                <Box
                  key={i}
                  p={2}
                  borderBottom={`1px solid ${theme.palette.divider}`}
                >
                  <Typography variant="body2">Carregando...</Typography>
                </Box>
              ))
            ) : queues.length > 0 ? (
              queues.map((queue) => (
                <Box
                  key={queue.id}
                  p={2}
                  borderBottom={`1px solid ${theme.palette.divider}`}
                  style={{
                    backgroundColor:
                      theme.palette.type === "dark" ? "#424242" : "#f3f3f3",
                  }}
                >
                  {/* Nome + Badges */}
                  <Typography
                    variant="subtitle1"
                    style={{ fontWeight: 600, marginBottom: 2 }}
                  >
                    {queue.name}
                  </Typography>
                  <QueueBadges queue={queue} classes={classes} />

                  {/* Cor */}
                  <div style={{ display: "flex", alignItems: "center", marginTop: 8, marginBottom: 4 }}>
                    <Typography
                      variant="body2"
                      style={{ fontWeight: 500, width: 70, color: theme.palette.text.secondary }}
                    >
                      Cor:
                    </Typography>
                    <span
                      className={classes.colorDot}
                      style={{ backgroundColor: queue.color, marginLeft: -8 }}
                      title={`Cor: ${queue.color}`}
                    />
                    <Typography variant="body2" style={{ marginLeft: 8 }}>
                      {queue.color}
                    </Typography>
                  </div>

                  {/* Ordem */}
                  <div style={{ display: "flex", marginBottom: 4 }}>
                    <Typography
                      variant="body2"
                      style={{ fontWeight: 500, width: 70, color: theme.palette.text.secondary }}
                    >
                      Ordem:
                    </Typography>
                    <Typography variant="body2">
                      {queue.orderQueue || "-"}
                    </Typography>
                  </div>

                  {/* Saudação */}
                  <div style={{ display: "flex", marginBottom: 4 }}>
                    <Typography
                      variant="body2"
                      style={{ fontWeight: 500, width: 70, color: theme.palette.text.secondary }}
                    >
                      Saudação:
                    </Typography>
                    <Typography
                      variant="body2"
                      style={{
                        flex: 1,
                        wordWrap: "break-word",
                        whiteSpace: "normal",
                        lineHeight: 1.4,
                      }}
                    >
                      {queue.greetingMessage || "-"}
                    </Typography>
                  </div>

                  {/* Ações */}
                  <Box mt={1} display="flex" justifyContent="flex-end" gap={1}>
                    <IconButton size="small" onClick={() => handleEditQueue(queue)}>
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="secondary"
                      onClick={() => {
                        setSelectedQueue(queue);
                        setConfirmModalOpen(true);
                      }}
                    >
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              ))
            ) : (
              <Box p={3} textAlign="center">
                <Typography color="textSecondary">
                  Nenhum departamento encontrado.
                </Typography>
              </Box>
            )}
          </Box>
        ) : (
          /* LAYOUT DESKTOP: Tabela */
          <Table className={classes.table} size="small">
            <TableHead>
              <TableRow>
                <TableCell align="left">{i18n.t("queues.table.name")}</TableCell>
                <TableCell align="center">{i18n.t("queues.table.color")}</TableCell>
                <TableCell align="center">{i18n.t("queues.table.orderQueue")}</TableCell>
                <TableCell align="center">{i18n.t("queues.table.greeting")}</TableCell>
                <TableCell align="center">{i18n.t("queues.table.actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {queues.length === 0 && !loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography color="textSecondary">
                      Nenhum departamento encontrado.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                queues.map((queue) => (
                  <TableRow key={queue.id}>
                    <TableCell align="left" style={{ verticalAlign: "middle" }}>
                      <span style={{ fontWeight: 500 }}>{queue.name}</span>
                      <QueueBadges queue={queue} classes={classes} />
                    </TableCell>
                    <TableCell align="center" style={{ verticalAlign: "middle" }}>
                      <Box display="flex" justifyContent="center">
                        <span
                          className={classes.colorDot}
                          style={{ backgroundColor: queue.color }}
                          title={`Cor: ${queue.color}`}
                        />
                      </Box>
                    </TableCell>
                    <TableCell align="center" style={{ verticalAlign: "middle" }}>
                      <Typography className={classes.orderCell}>
                        {queue.orderQueue || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" style={{ verticalAlign: "middle" }}>
                      <Tooltip title={queue.greetingMessage || ""} arrow>
                        <Typography
                          noWrap
                          className={classes.greetingCell}
                          color="textPrimary"
                        >
                          {queue.greetingMessage || "-"}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center" style={{ verticalAlign: "middle" }} className={classes.actionCell}>
                      <Box display="flex" justifyContent="center" alignItems="center" style={{ gap: 4 }}>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleEditQueue(queue)}
                        aria-label="Editar"
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="secondary"
                        onClick={() => {
                          setSelectedQueue(queue);
                          setConfirmModalOpen(true);
                        }}
                        aria-label="Excluir"
                      >
                        <DeleteOutline />
                      </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
              {loading && <TableRowSkeleton columns={5} />}
            </TableBody>
          </Table>
        )}
      </Paper>
    </MainContainer>
  );
};

export default Queues;
